import moment from 'moment';
import {
  AddHotelResponse,
  Brand,
  HotelDetails,
  HotelDetailsInput,
  HotelMediaContent,
  MiniHotelResponse,
  PropertyAirport,
  ReslinkType,
  EventDetailsInput,
  RateAmount,
  RateDetail,
  RoomTypes,
  AddHotelDttResponse,
  Product,
  AmountRule,
  InventoryType,
  RateModeCode,
  ComputationType,
  SubmitReslinkDttInput,
  HotelDetailsDttInput,
  EventDetailsDttInput,
  HotelGroupEventDetails,
  AvailabilityInfo,
  SourceCRS,
  HotelGroupEventDetailsForEmail,
  HotelGroupEventDetailsForEventList,
  GroupByGroupPeriod,
  InventoryStatisticsGroupByProductPeriod,
  GroupByProductPeriod,
} from '@marriott/mi-groups-graphql';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';
import { getBookingStats, getBookingStatsDtt, getGroupRates, getGroupRatesDtt, moveDecimalPoint } from './roomingList';
import { PropertyLabels } from '../organisms/reslink/ReslinkEventDetails/AddHotel/PropertyCard/PropertyCard.types';
import { ReslinkEventDetailsFormData } from '../organisms/reslink/ReslinkEventDetails/ReslinkEventDetails.types';
import {
  getPropertyAirportDistanceLabel,
  getPropertyItemValue,
  getReviewsUrl,
  getLocaleValue,
  formatUTCDate,
} from '../../utils';
import { ReservationLinkType } from '../organisms/reslink/DashboardContentTiles/ReslinkTypeModal/ReslinkTypeModal.types';
import {
  ARABIC,
  ARABIC_DOMAIN,
  DOMAIN_LOCALE_MAP,
  RESLINK_EMAIL_BRANCHIO_PATH,
  RESLINK_EMAIL_CF_PATH,
  SPANISH,
  SPANISH_DOMAIN,
  DEEP_VIEW,
  REGULAR_BRANCH_IO,
} from '../constants';
import { BranchIO } from '../organisms/reslink/EventList/EventList.types';
import { CF_LOCALE_MAP, DOMAIN_REGEX } from '../../constants';
import { EmailData } from '../organisms/reslink/ReslinkEmailInfo/ReslinkEmailInfo.types';
import { BookingStats } from '../organisms/rooming-list/PlannerNewEvent/EventBookingDetails/EventBookingDetails.types';

export const isGroupType = (reslinkType: ReservationLinkType) => reslinkType.toUpperCase() === ReservationLinkType.GRP;

export const isCorporateType = (reslinkType: ReservationLinkType) =>
  reslinkType.toUpperCase() === ReservationLinkType.CORP;

export const checkMarshaProperty = (sourceCRS?: SourceCRS) => sourceCRS === SourceCRS.MARSHA;

export const getPropertyData = (
  response: (AddHotelResponse & { miniHotelStartDate?: string }) | HotelDetails,
  propertyLabels: PropertyLabels,
  index: number
) => {
  const { property, miniHotelStartDate } = response;
  const { roomTypes, seoNickname, media, basicInformation, airports, reviews } = property;
  const { reviews: reviewsLabel, toAirport } = propertyLabels;

  const reviewsText = reviews?.numberOfReviews?.count
    ? `${getPropertyItemValue(reviews.numberOfReviews.count)} ${reviewsLabel}`
    : '';
  const reviewUrl = seoNickname ? getReviewsUrl(seoNickname, basicInformation?.brand?.id) : '#';
  const airportDistanceText = getPropertyAirportDistanceLabel(airports as PropertyAirport[], toAirport);
  const overview =
    basicInformation?.descriptions?.find(item => item.type.code === 'location')?.localizedText?.translatedText ||
    basicInformation?.descriptions?.[0]?.localizedText?.translatedText;

  const {
    isHousingProtected,
    bookByDate,
    fromDate,
    toDate,
    overriddenStartDate,
    overriddenEndDate,
    maxOccupancy,
    overriddenMaxOccupancy,
    rateRange,
    roomsData,
  } = (response as AddHotelResponse).searchMiniHotels
    ? getPartialPropertyDataOnAddHotel((response as AddHotelResponse).searchMiniHotels as MiniHotelResponse, roomTypes)
    : getPartialPropertyDataOnModifyReslink(response as HotelDetails);

  return {
    isHousingProtected: isHousingProtected as boolean,
    media: media as HotelMediaContent,
    brand: basicInformation?.brand as Brand,
    propertyId: property.id?.toString(),
    marshaCode: property.id?.toString()?.toUpperCase(),
    miniHotelCode: 'miniHotelId' in response ? response?.miniHotelId?.toUpperCase() : '',
    miniHotelStartDate,
    clusterCode: 'corporateCode' in response ? response.corporateCode : '',
    hotelName: basicInformation?.name || '',
    reviewsAndDistance: {
      rating: `${getPropertyItemValue(reviews?.stars?.count || '')}`,
      reviewCount: reviewsText,
      reviewLink: reviewUrl,
      distance: airportDistanceText,
    },
    description: overview || '',
    bookByDate,
    fromDate,
    toDate,
    overriddenStartDate,
    overriddenEndDate,
    maxOccupancy,
    overriddenMaxOccupancy,
    minRate: rateRange?.minRate,
    maxRate: rateRange?.maxRate,
    currency: rateRange?.currency,
    roomsData: roomsData || [],
    propertyIndex: index,
  };
};

const getPartialPropertyDataOnAddHotel = (searchMiniHotels: MiniHotelResponse, roomTypes: RoomTypes) => {
  const { node: miniHotel } = searchMiniHotels?.edges?.[0] || {};
  const { isHousingProtected, basicInformation, startDate: fromDate, endDate: toDate, rates } = miniHotel || {};
  const bookByDate = basicInformation?.cutoffDate;

  const maxOccupancy = getMaxOccupancy(rates || []);
  const rateRange = getRateRange(rates || []);
  const roomTypeCodes = roomTypes.edges.map(roomType => roomType.node.roomTypeCode);

  const roomsData = rates?.map(rate => {
    const { rateValue, currency } = getBaseRate(rate.rateAmounts);
    const matchingRoomTypeCode = roomTypeCodes.find(roomTypeCode => rate.roomType === roomTypeCode);

    return {
      fromDate: rate.startDate,
      toDate: rate.endDate,
      maxOccupancy: matchingRoomTypeCode ? rate.roomTypeDetail?.maxOccupancy : undefined,
      groupCode: rate.groupCode as string,
      roomType: matchingRoomTypeCode ? rate.roomTypeDetail?.name : undefined,
      roomRate: rateValue,
      currency,
      isRemoved: false,
    };
  });

  return {
    isHousingProtected: !!isHousingProtected,
    bookByDate,
    fromDate,
    toDate,
    overriddenStartDate: '',
    overriddenEndDate: '',
    maxOccupancy,
    overriddenMaxOccupancy: undefined,
    rateRange,
    roomsData,
  };
};

const getPartialPropertyDataOnModifyReslink = (hotelDetails: HotelDetails) => {
  const {
    groupCodes,
    maxOccupancy: hotelMaxOccupancy,
    overriddenStartDate,
    overriddenEndDate,
    miniHotelInfo,
    miniHotelStartDate,
  } = hotelDetails;
  const {
    isHousingProtected,
    basicInformation,
    startDate: fromDate,
    endDate: toDate,
    rates,
  } = miniHotelInfo?.[0]?.node || {};
  const bookByDate = basicInformation?.cutoffDate;

  const filteredRates = getFilteredRates(groupCodes, rates);
  const maxOccupancy = +hotelMaxOccupancy || getMaxOccupancy(filteredRates);
  const overriddenMaxOccupancy = +hotelMaxOccupancy || undefined;
  const rateRange = getRateRange(filteredRates);

  const roomsData = rates?.map(rate => {
    const { rateValue, currency } = getBaseRate(rate.rateAmounts);
    return {
      fromDate: rate.startDate,
      toDate: rate.endDate,
      maxOccupancy: rate?.roomTypeDetail?.maxOccupancy,
      roomType: rate?.roomTypeDetail?.name,
      groupCode: rate.groupCode as string,
      roomRate: rateValue,
      currency: currency,
      isRemoved: !groupCodes.includes(rate.groupCode as string),
    };
  });

  return {
    isHousingProtected: !!isHousingProtected,
    miniHotelStartDate,
    bookByDate,
    fromDate,
    toDate,
    overriddenStartDate,
    overriddenEndDate,
    maxOccupancy,
    overriddenMaxOccupancy,
    rateRange,
    roomsData,
  };
};

export const getPropertyDataOnAddHotel = (
  response: AddHotelDttResponse & { miniHotelStartDate?: string; groupCode: string },
  propertyLabels: PropertyLabels,
  index: number
) => {
  const { property, miniHotelStartDate, groupCode } = response;
  const { roomTypes, seoNickname, media, basicInformation, airports, reviews } = property;
  const { reviews: reviewsLabel, toAirport } = propertyLabels;

  const { groupContract, products, sourceCRS } =
    response.hotelOperations?.groupManagement.groupsMultipleGroupEventsAtProperty.groupBlocks[0] || {};
  const { timespans, releaseInventory, derivedFields, pricePlans } = groupContract || {};
  const { amountRules } = pricePlans?.[0].priceGridDetails || {};

  const reviewsText = reviews?.numberOfReviews?.count
    ? `${getPropertyItemValue(reviews.numberOfReviews.count)} ${reviewsLabel}`
    : '';
  const reviewUrl = seoNickname ? getReviewsUrl(seoNickname, basicInformation?.brand?.id) : '#';
  const airportDistanceText = getPropertyAirportDistanceLabel(airports as PropertyAirport[], toAirport);
  const overview =
    basicInformation?.descriptions?.find(item => item.type.code === 'location')?.text ||
    basicInformation?.descriptions?.[0]?.text;

  const roomTypeCodes = roomTypes.edges.map(roomType => roomType.node.roomTypeCode?.toLowerCase());

  const isMarshaProperty = sourceCRS === SourceCRS.MARSHA;
  const rateRange: { minRate: string | number; maxRate: string | number; currency: string } = {
    minRate: '-',
    maxRate: '-',
    currency: '',
  };
  let maxOccupancy = 0;
  const roomsData = amountRules?.flatMap(rule =>
    filterInventoryByLowestRate(rule.inventoryTypes, isMarshaProperty, null).map(inventoryType => {
      const { inventoryTypeCode, ratePlanCode, rateValue, currency } = getBaseRateInfoDtt(inventoryType);
      const matchingInventoryTypeCode = roomTypeCodes.find(
        roomTypeCode => inventoryTypeCode?.toLowerCase() === roomTypeCode
      );
      const matchingRoomTypeDetails = products?.find(
        product => product.inventoryTypeCode?.toLowerCase() === matchingInventoryTypeCode
      )?.roomTypeDetails;

      maxOccupancy = Math.max(maxOccupancy, matchingRoomTypeDetails?.maxOccupancy || 0);
      if (matchingRoomTypeDetails && rateValue !== '-') {
        rateRange.minRate =
          rateRange.minRate === '-' ? rateValue : Math.min(rateRange.minRate as number, rateValue as number);
        rateRange.maxRate =
          rateRange.maxRate === '-' ? rateValue : Math.max(rateRange.maxRate as number, rateValue as number);
      }
      rateRange.currency = rateRange.currency || currency;

      return {
        fromDate: rule?.timespan?.start,
        toDate: rule?.timespan?.end,
        maxOccupancy: matchingRoomTypeDetails?.maxOccupancy,
        groupCode: ratePlanCode ? `${groupCode}${ratePlanCode}` : '',
        inventoryTypeCode,
        roomType: matchingRoomTypeDetails?.name,
        roomRate: rateValue,
        currency,
        isRemoved: false,
      };
    })
  );

  return {
    isHousingProtected: !!derivedFields?.isHousingProtected,
    media: media as HotelMediaContent,
    brand: basicInformation?.brand as Brand,
    propertyId: property.id?.toString(),
    miniHotelStartDate,
    hotelName: basicInformation?.name || '',
    reviewsAndDistance: {
      rating: `${getPropertyItemValue(reviews?.stars?.count || '')}`,
      reviewCount: reviewsText,
      reviewLink: reviewUrl,
      distance: airportDistanceText,
    },
    description: overview || '',
    bookByDate: releaseInventory?.releaseInfo?.date,
    fromDate: timespans?.[0].start,
    toDate: timespans?.[0].end,
    overriddenStartDate: '',
    overriddenEndDate: '',
    maxOccupancy,
    overriddenMaxOccupancy: undefined,
    minRate: rateRange?.minRate,
    maxRate: rateRange?.maxRate,
    currency: rateRange?.currency,
    roomsData: roomsData || [],
    isMarshaProperty,
    propertyIndex: index,
  };
};

export const getPropertyDataOnModifyReslink = (
  response: HotelGroupEventDetails,
  propertyLabels: PropertyLabels,
  index: number
) => {
  const {
    inventoryTypeCodes,
    corporateCode,
    groupBlockCode,
    groupEventStartDate,
    overriddenEventStartDate,
    overriddenEventEndDate,
    overriddenMaxOccupancy,
    property,
  } = response;
  const { seoNickname, media, basicInformation, airports, reviews } = property;
  const { reviews: reviewsLabel, toAirport } = propertyLabels;

  const { groupContract, products, sourceCRS } = response.groupEventDetails?.groupBlock || {};
  const { timespans, releaseInventory, derivedFields, pricePlans } = groupContract || {};
  const { amountRules } = pricePlans?.[0].priceGridDetails || {};

  const reviewsText = reviews?.numberOfReviews?.count
    ? `${getPropertyItemValue(reviews.numberOfReviews.count)} ${reviewsLabel}`
    : '';
  const reviewUrl = seoNickname ? getReviewsUrl(seoNickname, basicInformation?.brand?.id) : '#';
  const airportDistanceText = getPropertyAirportDistanceLabel(airports as PropertyAirport[], toAirport);
  const overview =
    basicInformation?.descriptions?.find(item => item.type.code === 'location')?.text ||
    basicInformation?.descriptions?.[0]?.text;

  const isMarshaProperty = sourceCRS === SourceCRS.MARSHA;
  const enabledInventoryTypeCodes = isMarshaProperty
    ? getMarshaEnabledInventoryTypeCodes(inventoryTypeCodes, amountRules, groupBlockCode)
    : getACRSInventoryTypeCodes(amountRules);
  const filteredRates = getFilteredRatesDtt(enabledInventoryTypeCodes, amountRules, isMarshaProperty);
  const filteredProducts = getFilteredProductDtt(enabledInventoryTypeCodes, products);
  const maxOccupancy = +overriddenMaxOccupancy || getMaxOccupancyDtt(filteredProducts);
  const rateRange = getRateRangeDtt(filteredRates);
  const roomsData = amountRules?.flatMap(rule =>
    filterInventoryByLowestRate(rule.inventoryTypes, isMarshaProperty, null).map(inventoryType => {
      const { inventoryTypeCode, ratePlanCode, rateValue, currency } = getBaseRateInfoDtt(inventoryType);
      const matchingRoomTypeDetails = products?.find(
        product => product.inventoryTypeCode?.toLowerCase() === inventoryTypeCode?.toLowerCase()
      )?.roomTypeDetails;

      return {
        fromDate: rule?.timespan?.start,
        toDate: rule?.timespan?.end,
        maxOccupancy: matchingRoomTypeDetails?.maxOccupancy,
        groupCode: ratePlanCode ? `${groupBlockCode}${ratePlanCode}` : '',
        inventoryTypeCode,
        roomType: matchingRoomTypeDetails?.name,
        roomRate: rateValue,
        currency,
        isRemoved: !enabledInventoryTypeCodes?.includes(inventoryTypeCode),
      };
    })
  );

  return {
    isHousingProtected: !!derivedFields?.isHousingProtected,
    media: media as HotelMediaContent,
    brand: basicInformation?.brand as Brand,
    propertyId: property.id?.toString(),
    groupBlockCode: groupBlockCode || '',
    miniHotelStartDate: groupEventStartDate,
    corporateId: corporateCode || '',
    hotelName: basicInformation?.name || '',
    reviewsAndDistance: {
      rating: `${getPropertyItemValue(reviews?.stars?.count || '')}`,
      reviewCount: reviewsText,
      reviewLink: reviewUrl,
      distance: airportDistanceText,
    },
    description: overview || '',
    bookByDate: releaseInventory?.releaseInfo?.date,
    fromDate: timespans?.[0]?.start,
    toDate: timespans?.[0]?.end,
    overriddenStartDate: overriddenEventStartDate,
    overriddenEndDate: overriddenEventEndDate,
    maxOccupancy,
    overriddenMaxOccupancy: +overriddenMaxOccupancy || undefined,
    minRate: rateRange?.minRate,
    maxRate: rateRange?.maxRate,
    currency: rateRange?.currency,
    roomsData: roomsData || [],
    isMarshaProperty,
    propertyIndex: index,
  };
};

export const getFilteredRates = (groupCodes: string[], rates?: RateDetail[]) => {
  return rates?.filter(({ groupCode }) => groupCodes?.includes(groupCode as string)) || [];
};

export const getFilteredRatesDtt = (
  inventoryTypeCodes: string[],
  amountRules: AmountRule[],
  isMarshaProperty: boolean
) => {
  return (
    amountRules?.flatMap(
      rule => filterInventoryByLowestRate(rule.inventoryTypes, isMarshaProperty, inventoryTypeCodes) || []
    ) || []
  );
};

export const getFilteredProductDtt = (inventoryTypeCodes: string[], products: Product[]) => {
  return products?.filter(product => inventoryTypeCodes?.includes(product.inventoryTypeCode)) || [];
};

const getMaxOccupancy = (rates: RateDetail[]) => {
  return rates.reduce((maxGuest: number | undefined, roomType) => {
    return maxGuest
      ? Math.max(maxGuest, roomType?.roomTypeDetail?.maxOccupancy || 0)
      : roomType?.roomTypeDetail?.maxOccupancy;
  }, undefined);
};

const getMaxOccupancyDtt = (products: Product[]) => {
  return products?.reduce((maxGuest: number, product) => {
    return maxGuest
      ? Math.max(maxGuest, product?.roomTypeDetails.maxOccupancy || 0)
      : product?.roomTypeDetails?.maxOccupancy || 0;
  }, 0);
};

export const getRateRange = (rates: RateDetail[]) => {
  return rates.reduce(
    (acc: { minRate: string | number; maxRate: string | number; currency: string }, rate) => {
      const { rateValue, currency } = getBaseRate(rate.rateAmounts);
      if (rateValue !== '-') {
        acc.minRate = acc.minRate === '-' ? rateValue : Math.min(acc.minRate as number, rateValue as number);
        acc.maxRate = acc.maxRate === '-' ? rateValue : Math.max(acc.maxRate as number, rateValue as number);
      }
      acc.currency = acc.currency || currency;
      return acc;
    },
    { minRate: '-', maxRate: '-', currency: '' }
  );
};

export const getRateRangeDtt = (inventoryTypes: InventoryType[]) => {
  return inventoryTypes?.reduce(
    (acc: { minRate: string | number; maxRate: string | number; currency: string }, inventoryType) => {
      const { rateValue, currency } = getBaseRateInfoDtt(inventoryType);
      if (rateValue !== '-') {
        acc.minRate = acc.minRate === '-' ? rateValue : Math.min(acc.minRate as number, rateValue as number);
        acc.maxRate = acc.maxRate === '-' ? rateValue : Math.max(acc.maxRate as number, rateValue as number);
      }
      acc.currency = acc.currency || currency;
      return acc;
    },
    { minRate: '-', maxRate: '-', currency: '' }
  );
};

export const getBaseRate = (rateAmounts: RateAmount[]) => {
  const { amount } = rateAmounts.find(({ rateMode }) => rateMode?.code === 'base-rate') || {};
  const rateValue = moveDecimalPoint(amount?.value as number, amount?.valueDecimalPoint as number);
  return { rateValue, currency: amount?.currency || '' };
};

const getBaseRateInfoDtt = (inventoryType: InventoryType) => {
  const { code, ratePlanCode, amountValue } = inventoryType;
  const rateValue = moveDecimalPoint(amountValue?.value as number, amountValue?.numberOfDecimals as number);
  return { inventoryTypeCode: code, ratePlanCode, rateValue, currency: amountValue?.currencyCode || '' };
};

export const getMarshaEnabledInventoryTypeCodes = (
  inventoryTypeCodes: string[],
  amountRules: AmountRule[],
  groupBlockCode: string
) => {
  return amountRules?.flatMap(amountRule =>
    amountRule.inventoryTypes
      .filter(inventoryType => inventoryTypeCodes.includes(`${groupBlockCode}${inventoryType.ratePlanCode}`))
      .map(inventoryType => inventoryType.code)
  );
};

export const getACRSInventoryTypeCodes = (amountRules: AmountRule[]) => {
  return amountRules?.flatMap(amountRule => amountRule.inventoryTypes.map(inventoryType => inventoryType.code));
};

export const getCreateReslinkEventInput = (isGroup: boolean, data: ReslinkEventDetailsFormData, id: string) => {
  const {
    domain,
    associates,
    eventName: name,
    customImages,
    eventDescription: description,
    eventLink1,
    eventLinkName1,
    eventLink2,
    eventLinkName2,
    propertyCards,
  } = data;

  const eventLinks = [];
  eventLink1 && eventLinks.push({ link: eventLink1, name: eventLinkName1 });
  eventLink2 && eventLinks.push({ link: eventLink2, name: eventLinkName2 });

  const minStartDate: moment.Moment[] = [];
  const maxEndDate: moment.Moment[] = [];
  const hotelDetails: HotelDetailsInput[] = [];

  propertyCards.forEach(
    ({
      propertyId,
      miniHotelCode = '',
      miniHotelStartDate,
      clusterCode = null,
      fromDate = '',
      toDate = '',
      overriddenStartDate = '',
      overriddenEndDate = '',
      overriddenMaxOccupancy,
      roomsData = [],
    }) => {
      if (isGroup) {
        minStartDate.push(moment(overriddenStartDate || fromDate));
        maxEndDate.push(moment(overriddenEndDate || toDate));

        hotelDetails.push({
          propertyId,
          clusterCode,
          groupCodes: roomsData.filter(room => !room.isRemoved).map(room => room.groupCode),
          miniHotelId: miniHotelCode,
          miniHotelStartDate,
          overriddenStartDate,
          overriddenEndDate,
          overriddenMaxOccupancy,
        });
      } else {
        hotelDetails.push({
          propertyId,
          miniHotelStartDate: '',
          clusterCode,
          groupCodes: [],
        });
      }
    }
  );

  const eventDetails: EventDetailsInput = {
    localeURL: domain.value,
    associatedUsers: associates.split(/[;,]/g),
    name,
    customImageURLs: customImages,
    description,
    eventLinks,
    ...(isGroup
      ? {
          startDate: moment.min(minStartDate).format(getLocaleValue('hyphenatedDateWithMonthNoAndYear')),
          endDate: moment.max(maxEndDate).format(getLocaleValue('hyphenatedDateWithMonthNoAndYear')),
        }
      : {}),
  };

  if (id) {
    eventDetails.id = id;
  }

  return {
    reslinkType: isGroup ? ReslinkType.GRP : ReslinkType.CORP,
    eventDetail: eventDetails,
    hotelDetails,
  };
};

export const getCreateReslinkEventDttInput = (
  isGroup: boolean,
  data: ReslinkEventDetailsFormData,
  id: string,
  isBranchIoAppDownloadEnabled = false
): SubmitReslinkDttInput => {
  const {
    domain,
    associates,
    eventName: name,
    customImages,
    eventDescription,
    eventLink1,
    eventLinkName1,
    eventLink2,
    eventLinkName2,
    propertyCards,
  } = data;

  const description = removeTrailingEmptyParagraphs(eventDescription);

  const eventLinks = [];
  eventLink1 && eventLinks.push({ link: eventLink1, name: eventLinkName1 });
  eventLink2 && eventLinks.push({ link: eventLink2, name: eventLinkName2 });
  eventLinks.push({ name: '__branchIoType', link: isBranchIoAppDownloadEnabled ? DEEP_VIEW : REGULAR_BRANCH_IO });

  const minStartDate: moment.Moment[] = [];
  const maxEndDate: moment.Moment[] = [];
  const hotelGroupEventDetails: HotelDetailsDttInput[] = [];

  propertyCards.forEach(
    ({
      propertyId,
      groupBlockCode = '',
      miniHotelStartDate,
      corporateId = '',
      fromDate = '',
      toDate = '',
      overriddenStartDate = '',
      overriddenEndDate = '',
      overriddenMaxOccupancy,
      roomsData = [],
      isMarshaProperty,
    }) => {
      if (isGroup) {
        minStartDate.push(formatUTCDate(overriddenStartDate || fromDate));
        maxEndDate.push(formatUTCDate(overriddenEndDate || toDate));

        hotelGroupEventDetails.push({
          propertyId,
          groupInfo: {
            groupBlockCode,
          },
          groupEventStartDate: miniHotelStartDate,
          overriddenEventStartDate: overriddenStartDate,
          overriddenEventEndDate: overriddenEndDate,
          overriddenMaxOccupancy: overriddenMaxOccupancy?.toString(),
          inventoryTypeCodes: roomsData
            .filter(room => !room.isRemoved)
            .map(room => (isMarshaProperty ? (room.groupCode as string) : (room.inventoryTypeCode as string))),
        });
      } else {
        hotelGroupEventDetails.push({
          propertyId,
          corporateInfo: {
            corporateCode: corporateId,
          },
        });
      }
    }
  );

  const eventDetails: EventDetailsDttInput = {
    localeURL: domain.value,
    associatedUsers: associates.split(/[;,]/g),
    name,
    customImageUrls: customImages?.map(image => {
      return { link: image.link, alt: image.name };
    }),
    description,
    eventCustomLinks: eventLinks,
    ...(isGroup
      ? {
          startDate: moment.min(minStartDate).format(getLocaleValue('hyphenatedDateWithMonthNoAndYear')),
          endDate: moment.max(maxEndDate).format(getLocaleValue('hyphenatedDateWithMonthNoAndYear')),
        }
      : {}),
  };

  if (id) {
    eventDetails.id = id;
  }

  return {
    reslinkType: isGroup ? ReslinkType.GRP : ReslinkType.CORP,
    eventDetail: eventDetails,
    hotelGroupEventDetails,
  };
};

export const getlocalefromDomain = (url: string) => {
  if (url.toLowerCase().includes(SPANISH)) {
    return DOMAIN_LOCALE_MAP[SPANISH_DOMAIN];
  } else if (url.toLowerCase().includes(ARABIC)) {
    return DOMAIN_LOCALE_MAP[ARABIC_DOMAIN];
  } else {
    const matchingDomain = Object.keys(DOMAIN_LOCALE_MAP).find(domain =>
      url.toLowerCase().endsWith(domain.toLowerCase())
    );
    return DOMAIN_LOCALE_MAP[matchingDomain as string] ?? '';
  }
};

export const hasMissingInventoryDtt = (groupByGroupBlockData: GroupByGroupPeriod[]): boolean => {
  return groupByGroupBlockData.some(block =>
    block.groups.some(
      group =>
        group.inventoryTypeCounters.allocatedRoomsCounters.includes(0) ||
        group.inventoryTypeCounters.availabilityCounters.includes(0)
    )
  );
};

export const hasInventoryInDateRange = ({
  availability,
  startDate,
  endDate,
}: {
  availability: AvailabilityInfo[];
  startDate: string;
  endDate: string;
}) => {
  if (!availability || !startDate || !endDate) return false;

  for (const availabilityList of availability) {
    let date = startDate;
    for (const detail of availabilityList.availabilitydetails) {
      if (detail.available !== 0 && date === detail.startDate) {
        if (detail.endDate === endDate) {
          return true;
        }
        date = detail.endDate as string;
      }
    }
  }
  return false;
};

export const isBranchIO = (
  branchIo: BranchIO,
  locale: string,
  propertyType: string,
  eventType: string,
  isDtt = false,
  isBranchIoAssociateHubEnabled = false,
  isBranchIoAcrsEnabled = false,
  isCorporateBranchIoEnabled = false
) => {
  const { locale: branchIoLocale, corpLocales, url: branchIoUrl } = branchIo || {};
  return !!(
    locale &&
    branchIoUrl?.[window.location.host as string] &&
    ((branchIoLocale?.[locale.toLowerCase()] &&
      eventType === 'GRP' &&
      ((isDtt &&
        (((branchIo?.isEnabled || isBranchIoAssociateHubEnabled) &&
          (propertyType === SourceCRS.MARSHA || propertyType === SourceCRS.DSP)) ||
          ((branchIo?.isAcrsEnabled || isBranchIoAcrsEnabled) && propertyType === SourceCRS.ACRS))) ||
        (!isDtt && branchIo?.isBauEnabled && (propertyType === SourceCRS.MARSHA || propertyType === SourceCRS.DSP)))) ||
      (eventType === 'CORP' && isCorporateBranchIoEnabled && corpLocales?.[locale.toLowerCase()]))
  );
};

export const getBranchIoUrl = (url: string, id: string, key: string) => {
  try {
    return url?.replace(/{id}/g, id).replace(/{key}/g, key);
  } catch {
    return '';
  }
};

export const getPropertyType = (
  hotelDetails: HotelGroupEventDetailsForEmail[] | HotelGroupEventDetailsForEventList[]
): SourceCRS => {
  if (!hotelDetails || hotelDetails.length === 0) {
    return SourceCRS.MARSHA;
  }

  if (hotelDetails.length === 1) {
    return (hotelDetails[0]?.property?.otherPropertyInformation?.crsSystem as SourceCRS) ?? SourceCRS.MARSHA;
  }

  const hasACRS = hotelDetails.some(({ property }) => property?.otherPropertyInformation?.crsSystem === SourceCRS.ACRS);

  return hasACRS ? SourceCRS.ACRS : SourceCRS.MARSHA;
};

export const getEmailtemplate = async (
  isBranchIo = false,
  emailLocale: string,
  setEmailData: React.Dispatch<React.SetStateAction<EmailData>>
) => {
  const clientVars = useClientEnvVarsStore.getState().envVarsObject;
  const hostname = process.env['NEXT_PUBLIC_AEM_CF_HOST'] || clientVars['NEXT_PUBLIC_AEM_CF_HOST'];
  const { locale: localeMap, region } = CF_LOCALE_MAP[emailLocale] || { locale: '', region: '' };
  const url = `${hostname}${isBranchIo ? RESLINK_EMAIL_BRANCHIO_PATH : RESLINK_EMAIL_CF_PATH}`
    .replace(/{locale}/g, localeMap)
    .replace(/{region}/g, region);
  try {
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const result = await response.json();
    const {
      emailSubject: { value: emailSubject },
      messageBody: { value: messageBody },
      messageBodyCorp: { value: messageBodyCorp },
      forText: { value: forText },
      perNight: { value: perNight },
      lastDayToBook: { value: lastDayToBook },
    } = result.properties.elements;
    setEmailData({ emailSubject, messageBody, messageBodyCorp, forText, perNight, lastDayToBook });
  } catch (error) {
    console.error((error as Error).message);
  }
};

export const checkIsPropertyBlockFull = (data: AvailabilityInfo[]) => {
  if (!Array.isArray(data) || data.length === 0) return false;

  const propertyAvailablity = data.flatMap(item => {
    if (Array.isArray((item as AvailabilityInfo)?.availabilitydetails)) {
      return (item as AvailabilityInfo).availabilitydetails;
    } else {
      return [];
    }
  });

  if (!propertyAvailablity?.length) return false;

  return propertyAvailablity.every(detail => detail.available === 0);
};

export const checkForPropertyBlockFullDtt = (
  groupByProductPeriods: InventoryStatisticsGroupByProductPeriod[],
  inventoryTypeCodes: string[],
  startDate: string,
  endDate: string
) => {
  const hotelStartDate = startDate ? moment(startDate) : null;
  const hotelEndDate = endDate ? moment(endDate).subtract(1, 'day') : null;

  for (const productPeriod of groupByProductPeriods) {
    const periodStart = moment(productPeriod?.period?.start || '');

    for (const productGroup of productPeriod.groupByProducts) {
      for (const product of productGroup.products) {
        const inventoryCode = product.code;
        if (!inventoryTypeCodes.includes(inventoryCode)) continue;
        const availabilityCounters = product.productCounters.availabilityCounters || [];

        const currentDate = moment(periodStart);
        for (let i = 0; i < availabilityCounters?.length; i++) {
          if (
            (!hotelStartDate || currentDate.isSameOrAfter(hotelStartDate)) &&
            (!hotelEndDate || currentDate.isSameOrBefore(hotelEndDate)) &&
            availabilityCounters[i] > 0
          ) {
            return false;
          }
          currentDate.add(1, 'day');
        }
      }
    }
  }

  return true;
};

export const getEventTrackingData = (
  name: string,
  index: number,
  availability: AvailabilityInfo[] | undefined,
  rates: RateDetail[] | undefined
) => {
  const bookingStatsData = availability ? getBookingStats(availability) : {};
  const groupRatesData = rates && availability ? getGroupRates(rates, availability) : [];

  return {
    propertyName: name,
    propertyIndex: index,
    bookingStatsData: bookingStatsData as BookingStats,
    groupRatesData: groupRatesData,
  };
};

export const getEventTrackingDataDtt = (
  name: string,
  index: number,
  groupByGroupPeriods: GroupByGroupPeriod[] | undefined,
  amountRules: { inventoryTypes: InventoryType[] }[] | undefined,
  products: Product[] | undefined,
  groupByProductPeriods: GroupByProductPeriod[] | undefined,
  groupCode?: string,
  sourceCRS = SourceCRS.ACRS
) => {
  const bookingStatsData = groupByGroupPeriods ? getBookingStatsDtt(groupByGroupPeriods) : {};
  const groupRatesData =
    amountRules && products && groupByProductPeriods && sourceCRS
      ? getGroupRatesDtt(amountRules, products, groupByProductPeriods, groupCode, sourceCRS)
      : [];

  return {
    propertyName: name,
    propertyIndex: index,
    bookingStatsData: bookingStatsData as BookingStats,
    groupRatesData: groupRatesData,
  };
};

export const hasInventoryMissingDtt = (
  groupByGroupPeriods: GroupByGroupPeriod[],
  startDate: string,
  endDate: string
) => {
  for (const group of groupByGroupPeriods) {
    const periodStart = moment(group.period.start);
    const periodEnd = moment(group.period.end);
    const availabilityCounters = group.groups[0].inventoryTypeCounters.availabilityCounters || [];

    const hotelStartDate = startDate ? moment(startDate) : periodStart;
    const hotelEndDate = endDate ? moment(endDate).subtract(1, 'day') : periodEnd;

    const currentDate = moment(periodStart);
    for (let i = 0; i < availabilityCounters?.length; i++) {
      if (
        currentDate.isSameOrAfter(hotelStartDate) &&
        currentDate.isSameOrBefore(hotelEndDate) &&
        availabilityCounters?.[i] === 0
      ) {
        return true;
      }
      currentDate.add(1, 'day');
    }
  }

  return false;
};

export const getMissingInventoryDatesDtt = (groupByGroupPeriods: GroupByGroupPeriod[]): Date[] => {
  const bookingStats = getBookingStatsDtt(groupByGroupPeriods);
  const dateFormat = getLocaleValue('hyphenatedDateWithMonthNoAndYear');
  return bookingStats.availabilityList
    .filter(item => item.authorized === 0 || (item.authorized !== 0 && item.percentageOfRoomsSold === 100))
    .map(item => moment(item.startDate, dateFormat).toDate());
};

const filterInventoryByLowestRate = (
  inventoryList: InventoryType[],
  isMarshaProperty: boolean,
  allowedCodes: string[] | null = null
) => {
  const filteredByAllowedCodes = inventoryList.filter(
    item => allowedCodes === null || allowedCodes?.includes(item.code)
  );

  if (isMarshaProperty) {
    return filteredByAllowedCodes.filter(item => item.rateModeCode === RateModeCode.BASE_RATE);
  }

  const acrsInventoryList = new Map();
  const hasBaseRate = filteredByAllowedCodes.some(item => item.rateModeCode === RateModeCode.BASE_RATE);

  filteredByAllowedCodes
    .filter(
      item =>
        item.computationTypeEnum === ComputationType.FLAT &&
        (!hasBaseRate || item.rateModeCode === RateModeCode.BASE_RATE)
    )
    .forEach(item => {
      const currentRate = item.amountValue?.value || 0;
      const existingAcrsInventory = acrsInventoryList.get(item.code);
      const existingAcrsInventoryRate = existingAcrsInventory?.amountValue?.value || 0;

      if (!existingAcrsInventory || currentRate < existingAcrsInventoryRate) {
        acrsInventoryList.set(item.code, item);
      }
    });

  return Array.from(acrsInventoryList.values());
};

export const normalizeUrl = (url: string): string => {
  const trimmed = url.trim();

  if (/^(https?:\/\/|mailto:|tel:)/i.test(trimmed)) {
    return trimmed;
  }

  return `https://${trimmed}`;
};

export const isValidDomain = (url: string): boolean => {
  return DOMAIN_REGEX.test(url);
};

export const removeTrailingEmptyParagraphs = (html: string): string => {
  return html.replace(/(<p>(?:\s|&nbsp;|<br\s*\/?>)*<\/p>)+$/gi, '');
};
